import { getSeriesTypeLabel } from '@/src/utils/seriesType.utils'
import { t } from 'i18next'
import type { ISelectOption } from './selectOption.interface'

export type TimeSeriesReference = {
  id: string
  type: string
}

export const LOCATION_TOTAL_SERIES_TYPE = 'location_total'

export type LocationTotalReference = {
  locationId: number
  energyType: string
  unit: string
}

const LOCATION_TOTAL_REFERENCE_PATTERN = /^location-total:(\d+):([^:]+):([^:]+)$/

export const parseLocationTotalReference = (id?: string | null): LocationTotalReference | undefined => {
  const match = id ? LOCATION_TOTAL_REFERENCE_PATTERN.exec(id) : null
  if (!match) return undefined
  return { locationId: Number(match[1]), energyType: match[2], unit: match[3] }
}

export const formatLocationTotalLabel = (
  reference: LocationTotalReference,
  translate: (key: string) => string = t,
  locationName?: string
): string => {
  const label = `${translate('enpi.seu_selector.overall_consumption')} (${getSeriesTypeLabel(reference.energyType, translate)})`
  return locationName ? `${label} (${locationName})` : label
}

export const resolveTimeSeriesLabel = <T extends { id: string; label?: string | null; name?: string | null; title?: string | null }>(
  id?: string | null,
  series?: T[]
): string => {
  if (!id) return ''

  const metadata = series?.find(item => item.id === id)
  if (metadata) return metadata.label || metadata.name || metadata.title || id

  const locationTotal = parseLocationTotalReference(id)
  return locationTotal ? formatLocationTotalLabel(locationTotal) : id
}

export const isLocationTotalReference = (id?: string | null): boolean => parseLocationTotalReference(id) !== undefined

export const resolveTimeSeriesReference = <T extends { id: string; locationId?: number; type?: string }>(
  id: string,
  series?: T[]
): { id: string; locationId?: number; type?: string } => {
  const locationTotal = parseLocationTotalReference(id)
  if (locationTotal) {
    return { id, locationId: locationTotal.locationId, type: LOCATION_TOTAL_SERIES_TYPE }
  }

  const metadata = series?.find(item => item.id === id)
  return { id, locationId: metadata?.locationId, type: metadata?.type }
}

export type TimeSeriesOptionGroup = 'overall_consumption' | 'grid_import' | 'onsite_generation' | 'grid_export' | 'submeter' | 'other'

export const getTimeSeriesDisplayGroup = (group: TimeSeriesOptionGroup): TimeSeriesOptionGroup =>
  group === 'onsite_generation' ? 'grid_import' : group

export type TimeSeriesSelectionMetadata = {
  label: string
  energyType: string
  unit: string
  resolution: {
    enum: string
    label: string
    minutes: number
  }
  minDate: string | null
  maxDate: string | null
  dataQualityTimeSeriesIds: string[]
  selectable?: boolean
  disabledReason?: string
  formulaVersion?: string
}

export type TimeSeriesSelection = {
  reference: TimeSeriesReference
  locationId: number
  metadata: TimeSeriesSelectionMetadata
}

export type TimeSeriesSelectOption = ISelectOption<string> & {
  selection?: TimeSeriesSelection
  group?: TimeSeriesOptionGroup
  isVirtual?: boolean
  source?: string
}

export const getTimeSeriesSelectionKey = (reference: TimeSeriesReference, locationId: number, buildingId?: number): string =>
  `${reference.type}:${reference.id}:${locationId}:${buildingId ?? ''}`
